import { useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { LEVEL_LABELS } from "@book2class/shared";
import { api, ApiError } from "../api";
import { useAuth } from "../auth";
import { BrandMark, Spinner, ThemeToggle } from "../components/ui";

type PublicCourse = {
  course: {
    id: string;
    title: string;
    description: string | null;
    audience: string | null;
    level: string;
    workloadHours: number | null;
    coverUrl: string | null;
    price: string | null;
    enrollment: "open" | "restricted" | "paid";
  };
  counts: { lessons: number; questions: number; flashcards: number; cases: number };
  modules: Array<{
    position: number;
    title: string;
    summary: string | null;
    lessons: Array<{ title: string; readingMinutes: number | null }>;
  }>;
  institution: { name: string; slug: string; primaryColor: string | null; logoUrl: string | null };
  viewer: null | { sameInstitution: boolean; enrolled: boolean };
};

const msgOf = (err: unknown) => (err instanceof ApiError ? err.message : "Não foi possível concluir.");

/** Página pública de apresentação do curso, com matrícula ou compra. */
export function PublicCoursePage() {
  const { courseId = "" } = useParams();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [d, setD] = useState<PublicCourse | null>(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api
      .get<PublicCourse>(`/public/courses/${courseId}`)
      .then((x) => {
        setD(x);
        document.title = `${x.course.title} · ${x.institution.name}`;
      })
      .catch((err) => setError(msgOf(err)));
  }, [courseId, user]);

  if (error)
    return (
      <main className="page narrow">
        <p className="alert error">{error}</p>
      </main>
    );
  if (!d)
    return (
      <main className="page narrow">
        <Spinner />
      </main>
    );

  const c = d.course;
  const here = `/curso/${c.id}`;
  const minutes = d.modules.flatMap((m) => m.lessons).reduce((s, l) => s + (l.readingMinutes ?? 0), 0);

  async function act() {
    setError("");
    if (!user)
      return navigate(`/cadastro?instituicao=${d!.institution.slug}&voltar=${encodeURIComponent(here)}`);
    if (d!.viewer?.enrolled) return navigate(`/aluno/cursos/${c.id}`);
    setBusy(true);
    try {
      if (c.enrollment === "paid") {
        const r = await api.post<{ checkoutUrl: string }>("/payments/checkout", { courseId: c.id });
        window.location.assign(r.checkoutUrl);
        return;
      }
      await api.post(`/enrollments/courses/${c.id}`);
      navigate(`/aluno/cursos/${c.id}`);
    } catch (err) {
      setError(msgOf(err));
    } finally {
      setBusy(false);
    }
  }

  const cta = d.viewer?.enrolled
    ? "Ir para o curso"
    : c.enrollment === "paid"
      ? `Comprar por ${c.price}`
      : c.enrollment === "open"
        ? user
          ? "Matricular-se gratuitamente"
          : "Criar conta e começar"
        : null;

  return (
    <div className="public">
      <header className="topbar">
        <Link to={user ? "/" : "/entrar"} className="brand">
          {d.institution.logoUrl ? (
            <img src={d.institution.logoUrl} alt="" className="brand-logo" />
          ) : (
            <BrandMark />
          )}
          <span className="brand-name">{d.institution.name}</span>
        </Link>
        <div className="spacer" />
        <ThemeToggle />
        {!user && (
          <Link className="btn ghost small" to={`/entrar?voltar=${encodeURIComponent(here)}`}>
            Entrar
          </Link>
        )}
      </header>
      <main className="page">
        <section className="pub-hero">
          <div>
            <div className="eyebrow">
              Minicurso · {LEVEL_LABELS[c.level as keyof typeof LEVEL_LABELS] ?? c.level}
            </div>
            <h1>{c.title}</h1>
            {c.description && <p className="lead">{c.description}</p>}
            {c.audience && <p className="segment-meta">Para quem: {c.audience}</p>}
            <ul className="pub-facts">
              <li>
                <strong>{d.counts.lessons}</strong> aulas
              </li>
              <li>
                <strong>{c.workloadHours ?? Math.max(1, Math.round(minutes / 60))} h</strong> de carga horária
              </li>
              <li>
                <strong>{d.counts.questions}</strong> questões
              </li>
              <li>
                <strong>{d.counts.flashcards}</strong> flashcards
              </li>
              <li>
                <strong>Certificado</strong> com validação pública
              </li>
            </ul>
            {error && <p className="alert error">{error}</p>}
            {cta ? (
              <button className="btn big" disabled={busy} onClick={() => void act()}>
                {busy ? "Aguarde…" : cta}
              </button>
            ) : (
              <p className="alert">A matrícula neste curso é feita por convite da instituição.</p>
            )}
            {user && d.viewer && !d.viewer.sameInstitution && (
              <p className="segment-meta">
                Sua conta pertence a outra instituição. Para se matricular, crie uma conta pela instituição
                deste curso.
              </p>
            )}
          </div>
          <div className="pub-cover">
            {c.coverUrl ? <img src={c.coverUrl} alt="" /> : <span aria-hidden>{c.title.slice(0, 1)}</span>}
          </div>
        </section>
        <section className="pub-program">
          <h2>Conteúdo do curso</h2>
          <ol className="pub-modules">
            {d.modules.map((m) => (
              <li key={m.position} className="card card-pad">
                <div className="eyebrow">Módulo {m.position}</div>
                <h3>{m.title}</h3>
                {m.summary && <p className="segment-meta">{m.summary}</p>}
                <ul>
                  {m.lessons.map((l, i) => (
                    <li key={i}>
                      {l.title}
                      {l.readingMinutes ? (
                        <span className="segment-meta"> · {l.readingMinutes} min</span>
                      ) : null}
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ol>
        </section>
        <section className="card card-pad pub-how">
          <h2>Como funciona</h2>
          <p>
            Aulas curtas com pontos-chave e resumo visual, quizzes com correção imediata, flashcards com
            repetição espaçada, estudos de caso e um assistente de dúvidas que responde com base no conteúdo
            do curso. Ao concluir e ser aprovado(a) na avaliação final, você recebe um certificado com código
            de validação.
          </p>
        </section>
      </main>
    </div>
  );
}

type Payment = {
  id: string;
  status: string;
  amount: string;
  provider: string;
  course: { id: string; title: string } | null;
};

/** Retorno do checkout: acompanha a confirmação do pagamento e leva ao curso. */
export function PaymentStatusPage() {
  const { paymentId = "" } = useParams();
  const [p, setP] = useState<Payment | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    let stop = false;
    let tries = 0;
    const tick = async () => {
      try {
        const x = await api.get<Payment>(`/payments/${paymentId}`);
        if (stop) return;
        setP(x);
        if (x.status === "pending" && tries++ < 20) setTimeout(() => void tick(), 3000);
      } catch (err) {
        setError(msgOf(err));
      }
    };
    void tick();
    return () => {
      stop = true;
    };
  }, [paymentId]);
  if (error)
    return (
      <main className="page narrow">
        <p className="alert error">{error}</p>
      </main>
    );
  if (!p)
    return (
      <main className="page narrow">
        <Spinner />
      </main>
    );
  return (
    <main className="page narrow">
      <section className="card card-pad pay-card">
        <div className="eyebrow">Pagamento · {p.amount}</div>
        <h1>
          {p.status === "paid"
            ? "Pagamento confirmado!"
            : p.status === "pending"
              ? "Aguardando confirmação…"
              : "Pagamento não concluído"}
        </h1>
        <p className="segment-meta">{p.course?.title}</p>
        {p.status === "paid" && p.course ? (
          <Link className="btn" to={`/aluno/cursos/${p.course.id}`}>
            Começar o curso
          </Link>
        ) : p.status === "pending" ? (
          <p>Assim que o provedor confirmar, sua matrícula é liberada automaticamente.</p>
        ) : (
          p.course && (
            <Link className="btn ghost" to={`/curso/${p.course.id}`}>
              Tentar novamente
            </Link>
          )
        )}
      </section>
    </main>
  );
}

/** Checkout simulado (desenvolvimento e demonstração), no lugar da página do provedor. */
export function SimulatedCheckoutPage() {
  const { paymentId = "" } = useParams();
  const navigate = useNavigate();
  const [p, setP] = useState<Payment | null>(null);
  const [error, setError] = useState("");
  useEffect(() => {
    api
      .get<Payment>(`/payments/${paymentId}`)
      .then(setP)
      .catch((err) => setError(msgOf(err)));
  }, [paymentId]);
  const pay = async (outcome: "paid" | "failed") => {
    try {
      await api.post(`/payments/${paymentId}/simulate`, { outcome });
      navigate(`/pagamento/${paymentId}`);
    } catch (err) {
      setError(msgOf(err));
    }
  };
  return (
    <main className="page narrow">
      <section className="card card-pad pay-card">
        <div className="eyebrow">Checkout simulado</div>
        <h1>{p ? p.amount : "…"}</h1>
        <p className="segment-meta">{p?.course?.title}</p>
        <p className="alert">
          Ambiente de demonstração: nenhum valor é cobrado. Em produção, esta etapa acontece na página do
          provedor de pagamento (Mercado Pago).
        </p>
        {error && <p className="alert error">{error}</p>}
        <div className="actions" style={{ justifyContent: "flex-start" }}>
          <button className="btn" disabled={!p || p.status !== "pending"} onClick={() => void pay("paid")}>
            Simular pagamento aprovado
          </button>
          <button
            className="btn ghost"
            disabled={!p || p.status !== "pending"}
            onClick={() => void pay("failed")}
          >
            Simular recusa
          </button>
        </div>
      </section>
    </main>
  );
}
